import { useRef, useState } from 'react'
import { Droplet, Plus, Printer, Trash2 } from 'lucide-react'

import { Button } from '@/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Input } from '@/ui/input'
import { FilterMultiSelect } from '@/ui/filter-multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  OPERATOR_PHRASES,
  fieldOf,
  newKey,
  withField,
  withOperator,
  type BuilderCatalog,
  type ConditionNode,
} from '../alert-rules/alert-condition'
import type { ConsumableCondition } from './event-condition-model'

export function EventConditionFields({
  catalog,
  value,
  onChange,
  disabled,
}: {
  catalog: BuilderCatalog
  value: ConsumableCondition
  onChange: (value: ConsumableCondition) => void
  disabled: boolean
}) {
  const addFilter = useRef<HTMLButtonElement>(null)
  const [assetSelected, setAssetSelected] = useState(
    value.threshold.field !== '',
  )
  const [eventSelected, setEventSelected] = useState(
    value.threshold.field !== '',
  )
  const percent = fieldOf(catalog, 'supply.percentRemaining')
  const supplyFields = catalog.fields.filter(
    (field) =>
      field.id.startsWith('supply.') && field.id !== 'supply.percentRemaining',
  )
  function updateFilter(key: string, next: ConditionNode) {
    onChange({
      ...value,
      filters: value.filters.map((filter) =>
        filter.key === key ? next : filter,
      ),
    })
  }
  return (
    <fieldset disabled={disabled} className="flex flex-col gap-4">
      <legend className="mb-2 text-xs font-medium">What should happen</legend>
      <div className="border-input flex w-fit max-w-full flex-wrap items-center gap-1 rounded-md border text-xs">
        <span className="px-2">When</span>
        <Select
          value={assetSelected ? catalog.assetType : null}
          disabled={disabled}
          onValueChange={(asset) => {
            if (asset === catalog.assetType) setAssetSelected(true)
          }}
        >
          <SelectTrigger aria-label="Event asset type" className="w-auto">
            <SelectValue placeholder="Choose an asset type">
              {assetSelected ? 'Printer' : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={catalog.assetType}>
              <Printer aria-hidden className="size-3.5" />
              Printer
            </SelectItem>
          </SelectContent>
        </Select>
        {assetSelected && (
          <Select
            value={eventSelected ? catalog.event : null}
            disabled={disabled}
            onValueChange={(event) => {
              if (event === catalog.event) setEventSelected(true)
            }}
          >
            <SelectTrigger aria-label="Event type" className="w-auto">
              <SelectValue placeholder="Choose an event type">
                {eventSelected ? 'Consumable' : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={catalog.event}>
                <Droplet aria-hidden className="size-3.5" />
                Consumable
              </SelectItem>
            </SelectContent>
          </Select>
        )}
        {eventSelected && (
          <Select
            value={value.threshold.field || null}
            disabled={disabled}
            onValueChange={(field) => {
              if (field === percent?.id && field !== value.threshold.field)
                onChange({
                  ...value,
                  threshold: {
                    ...value.threshold,
                    field,
                    op: '',
                    value: '',
                  },
                })
            }}
          >
            <SelectTrigger aria-label="Event condition" className="w-auto">
              <SelectValue placeholder="Choose a condition">
                {value.threshold.field ? percent?.label : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {percent && (
                <SelectItem value={percent.id}>{percent.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        {value.threshold.field && (
          <Select
            value={value.threshold.op || null}
            onValueChange={(op) => {
              if (op)
                onChange({
                  ...value,
                  threshold: withOperator(catalog, value.threshold, op),
                })
            }}
            disabled={disabled}
          >
            <SelectTrigger
              aria-label="Percent remaining comparison"
              className="w-auto"
            >
              <SelectValue placeholder="Choose a comparator">
                {OPERATOR_PHRASES[value.threshold.op]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {percent?.operators
                .filter((op) => op !== 'in')
                .map((op) => (
                  <SelectItem key={op} value={op}>
                    {OPERATOR_PHRASES[op] ?? op}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
        {value.threshold.op && (
          <span className="inline-flex items-center gap-1">
            <Input
              aria-label="Percent remaining"
              inputMode="decimal"
              className="w-20"
              value={
                typeof value.threshold.value === 'string'
                  ? value.threshold.value
                  : ''
              }
              onChange={(event) =>
                onChange({
                  ...value,
                  threshold: {
                    ...value.threshold,
                    value: event.target.value,
                  },
                })
              }
            />
            <span>%</span>
          </span>
        )}
      </div>
      {value.threshold.field && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium">Which supplies</span>
            {value.filters.length > 1 && (
              <Select
                value={value.filterOperator}
                disabled={disabled}
                onValueChange={(op) => {
                  if (op === 'and' || op === 'or')
                    onChange({
                      ...value,
                      filterOperator: op,
                    })
                }}
              >
                <SelectTrigger
                  className="w-auto"
                  aria-label="Supply filters match"
                >
                  <SelectValue>
                    {value.filterOperator === 'and'
                      ? 'All filters'
                      : 'Any filter'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">All filters</SelectItem>
                  <SelectItem value="or">Any filter</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          {value.filters.map((filter, index) => {
            const field = fieldOf(catalog, filter.field)
            return (
              <div
                key={filter.key}
                className="flex flex-wrap items-center gap-1"
                role="group"
                aria-label={`Supply filter ${index + 1}`}
              >
                <Select
                  value={filter.field}
                  disabled={disabled}
                  onValueChange={(id) => {
                    if (id)
                      updateFilter(filter.key, withField(catalog, filter, id))
                  }}
                >
                  <SelectTrigger
                    className="w-auto"
                    aria-label={`Supply filter ${index + 1} field`}
                  >
                    <SelectValue>{field?.label ?? filter.field}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {supplyFields.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filter.op || null}
                  disabled={disabled}
                  onValueChange={(op) => {
                    if (op)
                      updateFilter(
                        filter.key,
                        withOperator(catalog, filter, op),
                      )
                  }}
                >
                  <SelectTrigger
                    className="w-auto"
                    aria-label={`Supply filter ${index + 1} comparison`}
                  >
                    <SelectValue placeholder="Choose a comparator">
                      {OPERATOR_PHRASES[filter.op]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {field?.operators.map((op) => (
                      <SelectItem key={op} value={op}>
                        {OPERATOR_PHRASES[op] ?? op}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filter.op && (
                  <SupplyFilterValue
                    filter={filter}
                    label={`Supply filter ${index + 1} value`}
                    disabled={disabled}
                    onChange={(next) =>
                      updateFilter(filter.key, {
                        ...filter,
                        value: next,
                      })
                    }
                  />
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove supply filter ${index + 1}`}
                  onClick={() => {
                    onChange({
                      ...value,
                      filters: value.filters.filter(
                        (item) => item.key !== filter.key,
                      ),
                    })
                    addFilter.current?.focus()
                  }}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
            )
          })}
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger
                ref={addFilter}
                disabled={disabled || supplyFields.length === 0}
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Add supply filter"
                  />
                }
              >
                <Plus aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {supplyFields.map((field) => (
                  <DropdownMenuItem
                    key={field.id}
                    onClick={() =>
                      onChange({
                        ...value,
                        filters: [
                          ...value.filters,
                          {
                            kind: 'condition',
                            key: newKey(),
                            field: field.id,
                            op: '',
                            value: '',
                          },
                        ],
                      })
                    }
                  >
                    {field.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-muted-foreground text-[11px]">
            Text values match exactly, including capitalization. Separate
            multiple values with commas.
          </p>
        </div>
      )}
    </fieldset>
  )
}

const SUPPLY_SUGGESTIONS: Record<string, { value: string; label: string }[]> = {
  'supply.color': [
    { value: 'black', label: 'Black' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'magenta', label: 'Magenta' },
    { value: 'yellow', label: 'Yellow' },
  ],
  'supply.type': [
    { value: 'toner', label: 'Toner' },
    { value: 'ink', label: 'Ink' },
  ],
}

function SupplyFilterValue({
  filter,
  label,
  disabled,
  onChange,
}: {
  filter: ConditionNode
  label: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const [custom, setCustom] = useState(false)
  const text = Array.isArray(filter.value)
    ? filter.value.join(', ')
    : filter.value
  const suggestions = SUPPLY_SUGGESTIONS[filter.field] ?? []
  if (
    custom ||
    suggestions.length === 0 ||
    !['in', 'eq', 'ne'].includes(filter.op)
  ) {
    return (
      <Input
        className="w-40"
        aria-label={label}
        value={text}
        disabled={disabled}
        placeholder={
          filter.op === 'in' ? 'Separate values with commas' : 'Enter a value'
        }
        onChange={(event) => onChange(event.target.value)}
      />
    )
  }
  const selected =
    filter.op === 'in'
      ? text
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : [text].filter(Boolean)
  const items = [
    ...suggestions,
    ...selected
      .filter((value) => !suggestions.some((item) => item.value === value))
      .map((value) => ({ value, label: value })),
  ]
  return (
    <div className="flex items-center gap-1">
      {filter.op === 'in' ? (
        <FilterMultiSelect
          label={label}
          values={selected}
          items={items}
          allLabel="Choose values"
          hideLabel
          onChange={(values) => onChange(values.join(', '))}
        />
      ) : (
        <Select
          value={text}
          disabled={disabled}
          onValueChange={(value) => {
            if (value !== null) onChange(value)
          }}
        >
          <SelectTrigger className="w-auto" aria-label={label}>
            <SelectValue placeholder="Choose a value">
              {items.find((item) => item.value === text)?.label ?? text}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => setCustom(true)}
      >
        Custom values
      </Button>
    </div>
  )
}
